/* eslint-disable no-loop-func */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import "@tomtom-international/web-sdk-maps/dist/maps.css";
import * as tt from "@tomtom-international/web-sdk-maps";
import * as ttServices from "@tomtom-international/web-sdk-services";
import debounce from "lodash/debounce";
import {
  IconChevronLeft,
  IconChevronRight,
  IconPentagonX,
  IconPolygon,
  IconTrashOff,
} from "@tabler/icons-react";
import toast from "react-hot-toast";
import {
  point,
  polygon as turfPolygon,
  booleanPointInPolygon,
} from "@turf/turf";

// Components
import SelectInput from "../../../components/Select";
import NormalSearch from "../../../components/common/NormalSearch";
import MultiSelectInput from "../../../components/MultiSelectInput";
import Input from "../../../components/Input";

// APIs
import { useWarehouseListData } from "../../../apis/warehouseApi";
import { useDistrictFetchData } from "../../../apis/locationApis";
import {
  useRouteCreateMutation,
  useRouteUpdateMutation,
} from "../../../apis/routeApis";
import ButtonStyled from "../../../components/common/ButtonStyled";
import { useNavigate } from "react-router-dom";
import MultiSelectInputNew from "../../../components/MultiSelectInputNew";

const MAX_ZOOM = 17;
const API_KEY = process.env.REACT_APP_TOMTOM_KEY || "SMRbRySjeUJB87ndJtBu3H8PaQ9D5aAN";

const COLORS = [
  "#EF4444", // red-500
  "#3B82F6", // blue-500
  "#10B981", // emerald-500
  "#F59E0B", // amber-500
  "#8B5CF6", // violet-500
  "#EC4899", // pink-500
  "#14B8A6", // teal-500
  "#6366F1", // indigo-500
  "#F97316", // orange-500
  "#22D3EE", // cyan-400
  "#A855F7", // purple-500
  "#84CC16", // lime-500
  "#F43F5E", // rose-500
  "#0EA5E9", // sky-500
  "#D946EF", // fuchsia-500
  "#34D399", // green-400
  "#EAB308", // yellow-500
  "#7C3AED", // violet-600
  "#F87171", // red-400
  "#60A5FA", // blue-400
];

interface Coordinate {
  longitude: number;
  latitude: number;
}

interface RoutePolygon {
  coordinates: Coordinate[];
  color: string;
}

interface DistrictOption {
  id: string;
  value: string;
  label: string;
}

interface FormValues {
  routeName: string;
  routeCode: string;
  warehouseId: string;
  city: string;
  district: DistrictOption[];
}

interface PolygonLayer {
  sourceId: string;
  fillLayerId: string;
  outlineLayerId: string;
  name: string;
  feature: {
    type: "Feature";
    geometry: {
      type: "Polygon" | "MultiPolygon";
      coordinates: number[][][] | number[][][][];
    };
    properties: {
      name: string;
      color?: string;
    };
  };
}

interface CustomPolygon {
  id: string;
  type: "Feature";
  geometry: {
    type: "Polygon";
    coordinates: number[][][];
  };
  properties?: {
    color?: string;
  };
}

interface Props {
  type?: string;
  routeDetails?: {
    id: string;
    warehouseId: string;
    cityId: string;
    districts: string[];
    routeName: string;
    routeCode: string;
    polygons: RoutePolygon[];
  };
  editMode?: boolean;
  onEditMode?: () => void;
  setActiveTab?: React.Dispatch<React.SetStateAction<any>>;
}

export default function AddRouteMap({
  type,
  routeDetails,
  editMode,
  onEditMode,
  setActiveTab,
}: Props) {
  const [selectedTool, setSelectedTool] = useState<string>("pan");
  const [selectedColor, setSelectedColor] = useState<string>("#00BCD4");
  const [colorDropdownOpen, setColorDropdownOpen] = useState<boolean>(false);
  const [selectedData, setSelectedData] = useState<{
    warehouseId: string;
    city: string;
    district: DistrictOption[];
  }>({
    warehouseId: "",
    city: "",
    district: [],
  });
  const [drawing, setDrawing] = useState<boolean>(false);
  const [polygonCoords, setPolygonCoords] = useState<[number, number][]>([]);
  const [polygonList, setPolygonList] = useState<CustomPolygon[]>([]);
  const [polygonLayers, setPolygonLayers] = useState<
    Record<string, PolygonLayer>
  >({});
  const [selectedPolygonId, setSelectedPolygonId] = useState<string | null>(
    null
  );
  const [cityBoundary, setCityBoundary] = useState<any>(null);
  const [polygon, setPolygon] = useState(null);

  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>();

  const { data: warehouses } = useWarehouseListData();
  const { mutate, isPending: isCreatePending } = useRouteCreateMutation();
  const { mutate: updateMutate, isPending: isUpdatePending } =
    useRouteUpdateMutation();
  const { data: districtData, refetch: districtRefetch } = useDistrictFetchData(
    selectedData.city
  );

  const mapElement = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<tt.Map | null>(null);
  const [mapLongitude, setMapLongitude] = useState<number>(-121.91599);
  const [mapLatitude, setMapLatitude] = useState<number>(37.36765);
  const [mapZoom, setMapZoom] = useState<number>(13);

  // Ref for latest polygonCoords
  const polygonCoordsRef = useRef(polygonCoords);
  useEffect(() => {
    polygonCoordsRef.current = polygonCoords;
  }, [polygonCoords]);

  // Initialize map
  useEffect(() => {
    if (!mapElement.current) return;

    const mapInstance = tt.map({
      key: API_KEY,
      container: mapElement.current,
      center: [mapLongitude, mapLatitude],
      zoom: mapZoom,
    });

    mapInstance.addControl(new tt.NavigationControl());

    mapInstance.on("load", () => {
      setMap(mapInstance);
    });

    return () => {
      mapInstance.remove();
    };
  }, []);

  // Load city boundary
  useEffect(() => {
    const fetchCityBoundary = async () => {
      const city = watch("city");
      if (!city) return;

      try {
        const searchRes = await fetch(
          `https://api.tomtom.com/search/2/search/${city}.json?key=${API_KEY}&typeahead=true&limit=1&adminDistrictType=City`
        );
        if (!searchRes.ok) throw new Error(`Search error: ${searchRes.status}`);

        const searchData = await searchRes.json();
        const result = searchData.results?.find(
          (e: any) => e?.type === "Geography"
        );

        if (!result) {
          console.warn("City not found with geometry");
          setCityBoundary(null);
          return;
        }

        const geometryId = result.dataSources?.geometry?.id;
        if (!geometryId) return;

        const geometryRes = await fetch(
          `https://api.tomtom.com/search/2/additionalData.json?key=${API_KEY}&geometries=${geometryId}`
        );
        if (!geometryRes.ok)
          throw new Error(`Geometry error: ${geometryRes.status}`);

        const geometryData = await geometryRes.json();
        const geometryFeatures =
          geometryData?.additionalData?.[0]?.geometryData?.features;

        if (geometryFeatures && geometryFeatures.length > 0) {
          const feature = geometryFeatures[0];
          if (
            feature.geometry &&
            (feature.geometry.type === "Polygon" ||
              feature.geometry.type === "MultiPolygon")
          ) {
            setCityBoundary(feature);
          }
        }
      } catch (error) {
        console.error("Error fetching city boundary:", error);
        setCityBoundary(null);
      }
    };

    fetchCityBoundary();
  }, [watch("city")]);

  // Add city boundary to map
  useEffect(() => {
    if (!map || !cityBoundary) return;

    if (map.getLayer("city-boundary")) map.removeLayer("city-boundary");
    if (map.getSource("city-boundary")) map.removeSource("city-boundary");

    map.addSource("city-boundary", {
      type: "geojson",
      data: cityBoundary,
    });

    map.addLayer({
      id: "city-boundary",
      type: "line",
      source: "city-boundary",
      paint: {
        "line-color": "#007bff",
        "line-width": 2,
      },
    });
  }, [map, cityBoundary]);

  // Add click handler for drawing polygons
  useEffect(() => {
    if (!map || !drawing) return;

    const handleClick = (e: any) => {
      if (!cityBoundary) return;

          const clickedPoint = point([e.lngLat.lng, e.lngLat.lat]);
          const cityPoly = turfPolygon(cityBoundary.geometry.coordinates);

          if (!booleanPointInPolygon(clickedPoint, cityPoly)) {
            toast.error("You cannot draw outside the city boundary");
            return;
      }

      const coords = e.lngLat.toArray();
      setPolygonCoords([...polygonCoordsRef.current, coords]);
    };

    map.on("click", handleClick);

    return () => {
      map.off("click", handleClick);
    };
  }, [map, drawing, cityBoundary]);

  // Update colors when selectedColor changes
  useEffect(() => {
    if (!map) return;

    // Update manual polygons
    polygonList.forEach((polygon) => {
      if (map.getLayer(polygon.id)) {
        map.setPaintProperty(
          polygon.id,
          "fill-color",
          polygon.properties?.color || selectedColor
        );
      }
      if (map.getLayer(`${polygon.id}-outline`)) {
        map.setPaintProperty(
          `${polygon.id}-outline`,
          "line-color",
          polygon.properties?.color || selectedColor
        );
      }
    });

    // Update district polygons
    Object.values(polygonLayers).forEach((layer) => {
      if (map.getLayer(layer.fillLayerId)) {
        map.setPaintProperty(layer.fillLayerId, "fill-color", selectedColor);
      }
      if (map.getLayer(layer.outlineLayerId)) {
        map.setPaintProperty(layer.outlineLayerId, "line-color", selectedColor);
      }
    });

    // Update drawing line color
    if (map.getLayer("drawing-line")) {
      map.setPaintProperty("drawing-line", "line-color", selectedColor);
    }
  }, [selectedColor, map, polygonList, polygonLayers]);

  // Show live line while drawing
  useEffect(() => {
    if (!map || polygonCoords.length < 2 || !drawing) return;

    const lineData = {
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: polygonCoords,
      },
    };

    if (map.getSource("drawing-line")) {
      (map.getSource("drawing-line") as any).setData(lineData);
    }
  }, [polygonCoords, drawing, map]);

  // Load route details
  useEffect(() => {
    if (!routeDetails || !warehouses) return;

    const warehouse = warehouses?.warehouses?.find(
      (w: any) => w.id === routeDetails.warehouseId
    );
    if (!warehouse) return;

    setSelectedData({
      warehouseId: warehouse.id,
      city: warehouse.city,
      district: [],
    });

    setValue("warehouseId", warehouse.name);
    setValue("city", warehouse.cityName);
    setValue("routeCode", routeDetails.routeCode);
    setValue("routeName", routeDetails.routeName);
  }, [routeDetails, warehouses, setValue]);

  // Load districts when city changes
  useEffect(() => {
    districtRefetch();
  }, [selectedData.city, districtRefetch]);

  // Set districts when district data is loaded
  useEffect(() => {
    if (!routeDetails || !districtData) return;

    const matchingDistricts = districtData
      .filter((district: any) => routeDetails.districts.includes(district.id))
      .map((district: any) => ({
        id: district.id,
        value: district.name,
        label: district.name,
      }));

    if (matchingDistricts.length > 0) {
      setValue("district", matchingDistricts);
      setSelectedData((prev) => ({
        ...prev,
        district: matchingDistricts,
      }));
      drawDistrictPolygons(matchingDistricts.map((d: any) => d.value));
    }
  }, [districtData, routeDetails, setValue]);

  // Load manual polygons when route details change
  useEffect(() => {
    if (!map || !routeDetails?.polygons) return;

    // Clear existing polygons
    polygonList.forEach((polygon) => {
      if (map.getLayer(polygon.id)) map.removeLayer(polygon.id);
      if (map.getSource(polygon.id)) map.removeSource(polygon.id);
    });

    // Convert and load new polygons
    const newPolygons: CustomPolygon[] = routeDetails.polygons.map(
      (polygon, index) => {
        const coordinates = polygon.coordinates.map((coord) => [
          coord.longitude,
          coord.latitude,
        ]);

        return {
          id: `route-polygon-${index}`,
          type: "Feature",
          geometry: {
            type: "Polygon",
            coordinates: [coordinates],
          },
          properties: {
            color: polygon.color,
          },
        };
      }
    );

    // Add to map
    if (!routeDetails?.districts || routeDetails.districts.length === 0) {
      newPolygons.forEach((polygon) => addPolygonToMap(polygon));
    }

    setPolygonList(newPolygons);

    // Zoom to polygons
    if (newPolygons.length > 0) {
      const bounds = new tt.LngLatBounds();
      newPolygons.forEach((polygon) => {
        polygon.geometry.coordinates[0].forEach((coord) => {
          bounds.extend(coord as [number, number]);
        });
      });
      map.fitBounds(bounds, { padding: 50 });
    }
  }, [map, routeDetails]);

  const drawDistrictPolygons = async (districts: string[]): Promise<void> => {
    if (!map) return;

    // Clear existing layers
    Object.values(polygonLayers).forEach((layer) => {
      if (map.getLayer(layer.fillLayerId)) map.removeLayer(layer.fillLayerId);
      if (map.getLayer(layer.outlineLayerId))
        map.removeLayer(layer.outlineLayerId);
      if (map.getSource(layer.sourceId)) map.removeSource(layer.sourceId);
    });

    const newPolygonLayers: Record<string, PolygonLayer> = {};
    const bounds = new tt.LngLatBounds();
    let hasBounds = false;

    for (const name of districts) {
      try {
        const searchRes = await fetch(
          `https://api.tomtom.com/search/2/search/${name} ${watch(
            "city"
          )?.toLowerCase()}.json?key=${API_KEY}&limit=5&countrySet=SA&queryType=NON_NEAR&typeahead=true&idxSet=Geo`
        );

        if (!searchRes.ok) continue;

        const searchData = await searchRes.json();
        const result = searchData.results?.find(
          (e: any) =>
            e?.type === "Geography" &&
            e?.entityType === "MunicipalitySubdivision"
        );

        if (!result) continue;

        let polygonGeometry;
        const geometryId = result.dataSources?.geometry?.id;

        if (geometryId) {
          try {
            const geometryRes = await fetch(
              `https://api.tomtom.com/search/2/additionalData.json?key=${API_KEY}&geometries=${geometryId}`
            );
            if (geometryRes.ok) {
              const geometryData = await geometryRes.json();
              const feature =
                geometryData?.additionalData?.[0]?.geometryData?.features?.[0];
              if (feature?.geometry) {
                polygonGeometry = feature.geometry;
              }
            }
          } catch (error) {
            console.warn(`Geometry fetch failed for "${name}":`, error);
          }
        }

        if (!polygonGeometry && result.viewport) {
          const viewport = result.viewport;
          polygonGeometry = {
            type: "Polygon",
            coordinates: [
              [
                [
                  viewport.topLeftPoint.lng || viewport.topLeftPoint.lon,
                  viewport.topLeftPoint.lat,
                ],
                [
                  viewport.btmRightPoint.lng || viewport.btmRightPoint.lon,
                  viewport.topLeftPoint.lat,
                ],
                [
                  viewport.btmRightPoint.lng || viewport.btmRightPoint.lon,
                  viewport.btmRightPoint.lat,
                ],
                [
                  viewport.topLeftPoint.lng || viewport.topLeftPoint.lon,
                  viewport.btmRightPoint.lat,
                ],
                [
                  viewport.topLeftPoint.lng || viewport.topLeftPoint.lon,
                  viewport.topLeftPoint.lat,
                ],
              ],
            ],
          };
        }

        if (polygonGeometry) {
          const sourceId = `polygon-${name}`;
          const fillLayerId = `polygon-fill-${name}`;
          const outlineLayerId = `polygon-outline-${name}`;

          const feature = {
            type: "Feature" as const,
            geometry: polygonGeometry,
            properties: {
              name,
              color: selectedColor,
            },
          };

          map.addSource(sourceId, {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features: [feature],
            },
          });

          map.addLayer({
            id: fillLayerId,
            type: "fill",
            source: sourceId,
            paint: {
              "fill-color": selectedColor,
              "fill-opacity": 0.6,
            },
          });

          map.addLayer({
            id: outlineLayerId,
            type: "line",
            source: sourceId,
            paint: {
              "line-color": selectedColor,
              "line-width": 2,
            },
          });

          newPolygonLayers[sourceId] = {
            sourceId,
            fillLayerId,
            outlineLayerId,
            name,
            feature,
          };

          if (polygonGeometry.type === "Polygon") {
            polygonGeometry.coordinates[0].forEach((coord: number[]) => {
              bounds.extend(coord as [number, number]);
              hasBounds = true;
            });
          }
        }
      } catch (error) {
        console.error(`Error processing "${name}":`, error);
      }
    }

    setPolygonLayers(newPolygonLayers);
    if (hasBounds) map.fitBounds(bounds, { padding: 50 });
  };

  const handleSearch = debounce((val: string): void => {
    ttServices.services
      .fuzzySearch({
        key: API_KEY,
        query: val,
      })
      .then((response: any) => {
        if (response.results?.length > 0) {
          const result = response.results[0];
          const { lat, lng } = result.position;
          setMapLongitude(lng);
          setMapLatitude(lat);
          map?.setCenter([lng, lat]);
          setMapZoom(13);
        }
      })
      .catch(console.error);
  }, 500);

  const handleCreate = async (data: FormValues): Promise<void> => {
    try {
      const warehouse = warehouses?.warehouses?.find(
        (w: any) => w.name === data.warehouseId
      );
      if (!warehouse) {
        toast.error("Warehouse not found");
        return;
      }

      const routeData = {
        warehouseId: warehouse.id,
        city: warehouse.city,
        district: data.district.map((d) => d.id),
        routeName: data.routeName,
        routeCode: data.routeCode,
        polygons: [] as RoutePolygon[],
      };

      // Validate required fields
      if (!routeData.routeName || !routeData.routeCode) {
        toast.error("Route name and code are required");
        return;
      }

      // Process district polygons
      Object.values(polygonLayers).forEach((layer: any) => {
        if (layer.feature.geometry.type === "Polygon") {
          const coordinates = layer.feature.geometry.coordinates[0].map(
            (coord: any) => ({
              longitude: coord[0],
              latitude: coord[1],
            })
          );
          routeData.polygons.push({
            coordinates,
            color: layer.feature.properties?.color || selectedColor,
          });
        }
      });

      // Process manual polygons
      polygonList.forEach((polygon) => {
        if (polygon.geometry.type === "Polygon") {
          const coordinates = polygon.geometry.coordinates[0].map((coord) => ({
            longitude: coord[0],
            latitude: coord[1],
          }));
          routeData.polygons.push({
            coordinates,
            color: polygon.properties?.color || selectedColor,
          });
        }
      });

      if (routeData.polygons.length === 0) {
        toast.error("Please create at least one polygon");
        return;
      }

      mutate(routeData, {
        onSuccess: () => {
          toast.success(type ? "Route updated" : "Route created");
          reset();
          if (setActiveTab) {
            setActiveTab("Routes List");
          }
          setPolygonList([]);
          setPolygonLayers({});
          navigate("/settings?tab=Route");
        },
        onError: (error) => {
          toast.error(error || "Route creation failed");
        },
      });
    } catch (error) {
      console.error("Error:", error);
      toast.error("An unexpected error occurred");
    }
  };

  const startDrawing = (): void => {
    if (!map) return;

    // Clear any existing drawing line
    if (map.getLayer("drawing-line")) map.removeLayer("drawing-line");
    if (map.getSource("drawing-line")) map.removeSource("drawing-line");

    // Add drawing line source and layer
    map.addSource("drawing-line", {
      type: "geojson",
      data: {
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: [],
        },
        properties: {},
      },
    });

    map.addLayer({
      id: "drawing-line",
      type: "line",
      source: "drawing-line",
      paint: {
        "line-color": selectedColor,
        "line-width": 2,
        "line-dasharray": [2, 2],
      },
    });

    setPolygonCoords([]);
    setDrawing(true);
  };

  const stopDrawing = (): void => {
    setDrawing(false);
    if (polygonCoords.length < 3) {
      toast.error("A polygon requires at least 3 points");
      if (map) {
        if (map.getLayer("drawing-line")) map.removeLayer("drawing-line");
        if (map.getSource("drawing-line")) map.removeSource("drawing-line");
      }
      setPolygonCoords([]);
      return;
    }

    // Close the polygon by adding the first point again
    const closedCoords = [...polygonCoords, polygonCoords[0]];

    const newPolygon: CustomPolygon = {
      id: `custom-polygon-${Date.now()}`,
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: [closedCoords],
      },
      properties: {
        color: selectedColor,
      },
    };

    // Add the polygon to the map
    addPolygonToMap(newPolygon);

    // Add to polygon list
    setPolygonList((prev) => [...prev, newPolygon]);
    setPolygonCoords([]);

    // Clean up drawing line
    if (map) {
      if (map.getLayer("drawing-line")) map.removeLayer("drawing-line");
      if (map.getSource("drawing-line")) map.removeSource("drawing-line");
    }
  };

  const handleDeletePolygon = (): void => {
    if (!selectedPolygonId || !map) return;
    if (map.getLayer(selectedPolygonId)) map.removeLayer(selectedPolygonId);
    if (map.getSource(selectedPolygonId)) map.removeSource(selectedPolygonId);
    if (map.getLayer(`${selectedPolygonId}-outline`)) {
      map.removeLayer(`${selectedPolygonId}-outline`);
    }
    setPolygonList((prev) => prev.filter((p) => p.id !== selectedPolygonId));
    setSelectedPolygonId(null);
  };

  // Polygon selection handler
  useEffect(() => {
    if (!map || polygonList.length === 0) return;

    const handlePolygonClick = (e: any) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: polygonList.map((p) => p.id),
      });

      if (features.length > 0) {
        const clickedPolygon = features[0];
        setSelectedPolygonId(clickedPolygon.layer.id);
      } else {
        setSelectedPolygonId(null);
      }
    };

    map.on("click", handlePolygonClick);
    return () => {
      map.off("click", handlePolygonClick);
    };
  }, [map, polygonList]);

  // Highlight selected polygon
  useEffect(() => {
    if (!map || !selectedPolygonId) return;

    // Add outline for selected polygon
    if (!map.getLayer(`${selectedPolygonId}-outline`)) {
      map.addLayer({
        id: `${selectedPolygonId}-outline`,
        type: "line",
        source: selectedPolygonId,
        paint: {
          "line-color": selectedColor,
          "line-width": 3,
        },
      });
    }

    return () => {
      if (map.getLayer(`${selectedPolygonId}-outline`)) {
        map.removeLayer(`${selectedPolygonId}-outline`);
      }
    };
  }, [selectedPolygonId, map, selectedColor]);

  const addPolygonToMap = (polygon: any) => {
    if (!map) return;

    // Remove existing layers if they exist
    if (map.getLayer(polygon.id)) map.removeLayer(polygon.id);
    if (map.getLayer(`${polygon.id}-outline`))
      map.removeLayer(`${polygon.id}-outline`);
    if (map.getSource(polygon.id)) map.removeSource(polygon.id);

    // Add the polygon source
    map.addSource(polygon.id, {
      type: "geojson",
      data: polygon,
    });

    // Add fill layer with color
    map.addLayer({
      id: polygon.id,
      type: "fill",
      source: polygon.id,
      paint: {
        "fill-color": polygon.properties?.color || selectedColor,
        "fill-opacity": 0.5,
      },
    });

    // Add outline layer with color
    map.addLayer({
      id: `${polygon.id}-outline`,
      type: "line",
      source: polygon.id,
      paint: {
        "line-color": polygon.properties?.color || selectedColor,
        "line-width": 2,
      },
    });
  };

  const handleUpdate = (data: any) => {
    try {
      const warehouse = warehouses?.warehouses?.find(
        (w: any) => w.name === data.warehouseId
      );
      if (!warehouse) {
        toast.error("Warehouse not found");
        return;
      }

      const routeData = {
        id: routeDetails?.id,
        warehouseId: warehouse.id,
        city: warehouse.city,
        district: data?.district?.map((d: any) => d.id) || [],
        routeName: data.routeName,
        routeCode: data.routeCode,
        polygons: [] as RoutePolygon[],
      };

      // Validate required fields
      if (!routeData.routeName || !routeData.routeCode) {
        toast.error("Route name and code are required");
        return;
      }

      // Process district polygons
      Object.values(polygonLayers).forEach((layer: any) => {
        if (layer.feature.geometry.type === "Polygon") {
          const coordinates = layer.feature.geometry.coordinates[0].map(
            (coord: any) => ({
              longitude: coord[0],
              latitude: coord[1],
            })
          );
          routeData.polygons.push({
            coordinates,
            color: layer.feature.properties?.color || selectedColor,
          });
        }
      });

      // Process manual polygons
      polygonList.forEach((polygon) => {
        if (polygon.geometry.type === "Polygon") {
          const coordinates = polygon.geometry.coordinates[0].map((coord) => ({
            longitude: coord[0],
            latitude: coord[1],
          }));
          routeData.polygons.push({
            coordinates,
            color: polygon.properties?.color || selectedColor,
          });
        }
      });

      if (routeData.polygons.length === 0) {
        toast.error("Please create at least one polygon");
        return;
      }

      updateMutate(routeData, {
        onSuccess: () => {
          toast.success(type ? "Route updated" : "Route created");
          reset();
          if (setActiveTab) {
            setActiveTab("Routes List");
          }
          setPolygonList([]);
          setPolygonLayers({});
          navigate("/settings?tab=Route");
        },
        onError: (error) => {
          toast.error(
            error?.response?.data?.message || "Route update request failed"
          );
        },
      });
    } catch (error) {
      console.error("Error:", error);
      toast.error("Route update request failed");
    }
  };

  // Update all polygons' color in state when selectedColor changes
  useEffect(() => {
    // Update manual polygons
    setPolygonList((prevPolygons) =>
      prevPolygons.map((polygon) => ({
        ...polygon,
        properties: {
          ...polygon.properties,
          color: selectedColor,
        },
      }))
    );

    // Update district polygons
    setPolygonLayers((prevLayers) => {
      const updatedLayers: Record<string, PolygonLayer> = {};
      Object.entries(prevLayers).forEach(([key, layer]) => {
        updatedLayers[key] = {
          ...layer,
          feature: {
            ...layer.feature,
            properties: {
              ...layer.feature.properties,
              color: selectedColor,
            },
          },
        };
      });
      return updatedLayers;
    });
  }, [selectedColor]);

  const navigate = useNavigate();
  return (
    <section className="grid grid-cols-12 gap-x-6 h-auto lg:h-[calc(100vh-6.5rem)] overflow-visible  bg-[#F7F6F2] p-4 rounded-[20px] space-y-4 z-0">
      <div className="col-span-12 lg:col-span-3 w-auto bg-[#F7F6F2] pr-4 space-y-4 ">
        <div className="flex items-center gap-2 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 border border-[#D2D2D2] rounded-full"
          >
            <IconChevronLeft strokeWidth={1.5} size={24} />
          </button>
          <h2 className="text-base font-medium">
            {routeDetails?.routeCode || "Add Route"}
          </h2>
        </div>
        <div className="space-y-4">
          <SelectInput
            labelClass="px-2"
            name="warehouseId"
            label="Warehouse"
            placeholder="Warehouse"
            selectOptions={
              warehouses?.warehouses?.map((w: any) => ({
                id: w.id,
                value: w.name,
                text: w.name,
              })) || []
            }
            register={register}
            error={errors.warehouseId}
            clearErrors={clearErrors}
            validation={{ required: "Warehouse is required" }}
            disabled={
              !warehouses ||
              warehouses?.warehouses?.length === 0 ||
              (!!type && !editMode)
            }
            value={watch("warehouseId")}
            setValue={(val: string) => {
              const warehouse = warehouses?.warehouses?.find(
                (w: any) => w.name === val
              );
              if (warehouse) {
                setValue("warehouseId", warehouse.name);
                setValue("city", warehouse.cityName);
                setValue("district", []);
                setSelectedData({
                  warehouseId: warehouse.id,
                  city: warehouse.city,
                  district: [],
                });
                handleSearch(warehouse.cityName);
                clearErrors("city");
                clearErrors("district");
              }
            }}
          />

          <Input
            name="city"
            type="text"
            labelClass="px-2"
            title="City"
            register={register}
            error={errors.city}
            validation={{ required: "City is required" }}
            disabled
            className="text-sm text-[#212121]"
          />

          {/* District + Color Picker */}
          <div className="relative ">
            <MultiSelectInputNew
              labelClass="px-2"
              label="District"
              name="district"
              options={
                districtData?.map((d: any) => ({
                  id: d.id,
                  value: d.name,
                  label: d.name,
                })) || []
              }
              value={selectedData.district}
              onChange={(selected: any) => {
                setValue("district", selected);
                setSelectedData((prev) => ({ ...prev, district: selected }));
                drawDistrictPolygons(selected.map((d: any) => d.value));
              }}
              disabled={!!type && !editMode}
            />
            <div className="absolute top-[36px] left-2 z-20">
              <button
                className="w-4 h-4 border rounded"
                style={{ backgroundColor: selectedColor }}
                onClick={() => setColorDropdownOpen(!colorDropdownOpen)}
              />
              {colorDropdownOpen && (
                <div className="absolute w-40 top-8 left-0 bg-white shadow-md border rounded p-1 grid grid-cols-6 gap-1">
                  {COLORS.map((clr) => (
                    <div
                      key={clr}
                      className="w-5 h-5 rounded cursor-pointer border"
                      style={{ backgroundColor: clr }}
                      onClick={() => {
                        setSelectedColor(clr);
                        setColorDropdownOpen(false);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <Input
            name="routeName"
            type="text"
            labelClass="px-2"
            title="Route Name"
            register={register}
            error={errors.routeName}
            validation={{
              required: "Route name is required",
              pattern: {
                value: /^(?=.*[A-Za-z])[A-Za-z0-9\s-]+$/,
                message:
                  "Route Name must include at least one letter (not only numbers)",
              },
              validate: (value) => {
                const trimmed = value.trim();
                if (trimmed.length < 3) return "Minimum 3 characters";
                if (trimmed.length > 30) return "Maximum 30 characters";
                return true;
              },
            }}
            disabled={!!type && !editMode}
            className="text-sm text-[#212121]"
          />

          <Input
            name="routeCode"
            labelClass="px-2"
            type="text"
            title="Route Code"
            register={register}
            error={errors.routeCode}
            validation={{
              required: "Route code is required",
              validate: (value) => {
                const trimmed = value.trim();

                if (trimmed.length < 3) return "Minimum 3 characters";
                if (trimmed.length > 30) return "Maximum 30 characters";

                // ❌ Do not allow only numbers
                if (/^\d+$/.test(trimmed))
                  return "Route code cannot be only numbers";

                return true;
              },
            }}
            disabled={!!type && !editMode}
            className="text-sm text-[#212121]"
          />
        </div>

        <div className="">
          <ButtonStyled
            value={
              !type ? "Add Route" : editMode ? "Update Route" : "Edit Route"
            }
            onClick={() => {
              if (!editMode && onEditMode) onEditMode();
              else if (editMode && onEditMode) handleSubmit(handleUpdate)();
              else handleSubmit(handleCreate)();
            }}
            // extraClasses="bg-[#FBD037] text-black w-full py-2 font-semibold rounded-lg hover:opacity-90"
            isPending={isUpdatePending || isCreatePending}
          />
        </div>
      </div>

      {/* Map Section */}
      <div className="col-span-12 lg:col-span-9 relative ">
        <div
          ref={mapElement}
          className="mapDiv"
          style={{
            width: "100%",
            height: "100%",
            minHeight: "500px",
            borderRadius: "20px",
          }}
        />

        <div className="absolute top-5 left-5 flex flex-col gap-2 bg-white rounded-xl shadow z-50 p-2">
          {selectedData.district.length === 0 && (
            <button
              onClick={() => {
                if (watch("city")) {
                  setSelectedTool(selectedTool === "select" ? "" : "select");
                  if (!drawing) startDrawing();
                  else stopDrawing();
                } else toast("Please select a warehouse first");
              }}
              className={`p-3 rounded ${
                selectedTool === "select"
                  ? "bg-[#FBD037]"
                  : "hover:bg-[#FBD037]"
              }`}
            >
              {selectedTool === "select" ? <IconPentagonX /> : <IconPolygon />}
            </button>
          )}
          {selectedPolygonId && (
            <button
              onClick={handleDeletePolygon}
              className={`p-3 rounded ${
                selectedTool === "delete"
                  ? "bg-[#FBD037]"
                  : "hover:bg-[#FBD037]"
              }`}
            >
              <IconTrashOff />
            </button>
          )}
        </div>

        {/* Search Bar */}
        <div className="absolute top-4 right-12 z-40">
          <NormalSearch
            className="w-[400px] shadow rounded-full"
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>

        {/* Color Strip */}
        <div className="absolute top-20 right-12 bg-white rounded shadow p-2 flex flex-col items-center gap-2 z-40">
          <div className="max-h-[260px] overflow-y-auto space-y-2 pr-2">
            {COLORS.map((color, idx) => (
              <div
                key={idx}
                onClick={() => setSelectedColor(color)}
                className="w-5 h-5 rounded cursor-pointer border"
                style={{
                  backgroundColor: color,
                  borderColor:
                    selectedColor === color ? "black" : "transparent",
                  borderWidth: 2,
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
